import { IBaseCommand } from './commands/baseCommand';
import { CommandFactory } from './commandFactory';
import { RequestReplyService } from '../remote-control-ng/services/requestReplyService';
import { CloseHandler, ConnectHandler, ErrorHandler, IMessageHandler, MessageType } from './definitions';
import { RcContext } from '../remote-control-ng/context/rcContext';
import { Injector } from '@angular/core';

enum RCPacketOffsets {
    PACKET_SIZE = 0,
    SIGNATURE = 4,
    COMMAND_POSITION = 5,
    FLAGS = 6
}

interface ICommandHandler {
    command: IBaseCommand;
}

interface ICommandMapping {
    [command: number]: ICommandHandler;
}

export interface ISignatureMapping {
    [signature: number]: ICommandMapping;
}

export class MessageHandler implements IMessageHandler {
    private commandFactory: CommandFactory;
    private master: IMessageHandler;
    private messageCallback: (event: Event) => void;
    private connectCallback: ConnectHandler;
    private closeCallback: CloseHandler;
    private errorCallback: ErrorHandler;

    /** Injector is required if using a command factory. If master handler is supplied,
     * this is a secondary handler forwarding messages that doesn't require the Injector
     */
    constructor(injector: Injector, master?: IMessageHandler, isStreaming?: boolean) {
        // master allows forwarding messages to a different handler,
        // needed for the secondary socket where a message
        // could arrive on either one of the sockets/handled.
        if (!master) {
            this.commandFactory = new CommandFactory(injector, isStreaming);
            this.messageCallback = this.handleMessage;
        } else {
            this.master = master;
            this.messageCallback = this.forwardMessage;
        }
    }

    public setOnConnect = (callback: ConnectHandler) => {
        this.connectCallback = callback;
    }

    public setOnClose = (callback: CloseHandler) => {
        this.closeCallback = callback;
    }

    public setOnMessage = (callback: (event: Event) => void) => {
        this.messageCallback = callback;
    }

    public setOnError = (callback: ErrorHandler) => {
        this.errorCallback = callback;
    }

    public onMessage = (event: Event) => this.messageCallback(event);

    public onConnect = (event: Event) => {
        if (this.connectCallback) {
            this.connectCallback();
        }
    }

    public onClose = (event: CloseEvent) => {
        if (!!RcContext.instance.deviceId) {
            (<any>event).deviceId = RcContext.instance.deviceId;
        }
        if (this.closeCallback) {
            this.closeCallback(event);
        }
    }

    public onError = (event: ErrorEvent) => {
        if (this.errorCallback) {
            this.errorCallback(event.message);
        }
    }

    private handleMessage = (event: Event) => {
        const eventData = (<any>event)?.data;
        if (!eventData) {
            throw new Error(!event ? 'Socket event cannot be NULL' : 'Invalid socket event: data is missing');
        }

        const binData = new Uint8Array(eventData);
        const commandObject = this.commandFactory.createCommandInstance(binData);
        if (commandObject !== undefined) {
            commandObject.process();
        }
    }

    private forwardMessage = (event: Event) => this.master.onMessage(event);
}
